import type { APICallOptions } from 'labstep-web/models';
import type { EntityName } from 'labstep-web/services/schema/schema';
import { updateEntity } from 'labstep-web/state/actions/entity';
import { selectUpdateEntityAllStatuses } from 'labstep-web/state/selectors/entity';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { OwnProps } from './types';

// Create a separate type for the "Any" version that includes the id parameter
type EntityUpdateAnyContainerChildrenProps<T extends EntityName> = {
  statuses: any;
  update: (
    id: string | number,
    body: Record<string, unknown>,
    options?: APICallOptions,
  ) => void;
};

export const EntityUpdateAnyContainer = <
  T extends EntityName = EntityName,
>({
  children,
  entityName,
  batch,
  optimistic,
}: OwnProps<T> & {
  children: (
    props: EntityUpdateAnyContainerChildrenProps<T>,
  ) => React.ReactNode;
}): React.ReactElement => {
  const dispatch = useDispatch();

  // Use selector to get statuses
  const statuses = useSelector((state) =>
    selectUpdateEntityAllStatuses(state, entityName),
  );

  // Memoized update function
  const update = useCallback(
    (
      id: string | number,
      body: Record<string, unknown>,
      options?: APICallOptions,
    ) => {
      dispatch(
        updateEntity(entityName, id, body, {
          batch,
          optimistic,
          ...options,
        }),
      );
    },
    [dispatch, entityName, batch, optimistic],
  );

  return <>{children({ statuses, update })}</>;
};
